import { FieldArray, Form, Formik } from 'formik';
import React, { useContext, useEffect, useState } from 'react';
import AvatarEditor from 'react-avatar-editor';
import { useHistory, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { apiAffiliateGet, apiAffiliateSave } from '../../api/api-affiliates';
import { apiSourcesGet } from '../../api/api-sources';
import { TourTypeContext } from '../../contexts/tour-type-context';
import { companyList } from '../../helpers/constants';
import { generateAlphaNumericCode, generateCode } from '../../helpers/verification-code-helpers';
import { Button, ButtonSubmit, FontAwesomeIcon, InputCheckbox, InputDate, InputSelect, InputText, PageTitle } from '../common';
import { editPageActions } from './page-actions';

const AffiliateEdit = () => {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState({});
    const [error, setError] = useState(undefined);
    const [editorRef, setEditorRef] = useState(null);
    const [image, setImage] = useState();
    const [scale, setScale] = useState(1);
    const [rotate, setRotate] = useState(0);
    const [uploaded, setUploaded] = useState(false);
    const [sources, setSources] = useState([]);
    const { tourTypeId } = useContext(TourTypeContext);
    const history = useHistory();
    const { id } = useParams();

    const formSchema = Yup.object().shape({
        affiliateName: Yup.string().required('Required'),
        affiliateEmail: Yup.string().email('Invalid').required('Required'),
        affiliateEmail2: Yup.string().nullable().email('Invalid'),
        companyId: Yup.string().required('Required'),
        sourceId: Yup.string().required('Required'),
        verificationCode: Yup.string().required('Required'),
        codes: Yup.array().of(
            Yup.object().shape({
                affiliateCode: Yup.string()
                    .matches(/^[a-zA-Z0-9]*$/, 'Alphanumeric only')
                    .max(12, 'Must be 12 characters or fewer')
                    .required('Required'),
                discountCode: Yup.string().max(50, 'Must be 50 characters or fewer').nullable(),
            })
        ),
    });

    useEffect(() => {
        const LoadData = async () => {
            setSources(await apiSourcesGet(tourTypeId));

            if (id) {
                const result = await apiAffiliateGet(tourTypeId, id);

                if (!result.codes) {
                    result.codes = [];
                }

                setData(result);
            } else {
                //Defaults
                setData({
                    affiliateName: '',
                    affiliateEmail: '',
                    affiliateEmail2: '',
                    companyId: '',
                    verificationCode: generateCode(),
                    isDisabled: false,
                    viewOwnBookings: false,
                    codes: [],
                });
            }

            setLoading(false);
        };

        LoadData();
    }, [tourTypeId, id]);

    const regenCode = (setFieldValue) => {
        setFieldValue('verificationCode', generateCode());
    };

    const handleNewImage = (e) => {
        setImage(e.target.files[0]);
        setUploaded(true);
    };

    const handleScale = (e) => {
        const scale = parseFloat(e.target.value);
        setScale(scale);
    };

    const rotateLeft = (e) => {
        e.preventDefault();

        setRotate(rotate - 90);
    };

    const rotateRight = (e) => {
        e.preventDefault();

        setRotate(rotate + 90);
    };

    const setRef = (editor) => {
        if (editor) setEditorRef(editor);
    };

    return (
        <div className="max-w-screen-lg my-2 sm:m-4 sm:rounded-lg shadow-md bg-white sm:px-4 py-4 md:mx-auto">
            <PageTitle title="Affiliates" actions={editPageActions} />

            {!loading && (
                <Formik
                    initialValues={data}
                    validationSchema={formSchema}
                    onSubmit={async (values) => {
                        setError(undefined);

                        const base64String = uploaded ? editorRef.getImageScaledToCanvas().toDataURL() : null;

                        const result = await apiAffiliateSave(
                            tourTypeId,
                            id,
                            values.affiliateName,
                            values.affiliateEmail,
                            values.affiliateEmail2,
                            values.companyId,
                            values.sourceId,
                            values.verificationCode,
                            base64String,
                            values.viewOwnBookings,
                            values.isDisabled,
                            values.codes
                        );

                        if (result.success) {
                            history.push('/affiliates');
                        }

                        setError(result.exception?.data || 'An error occurred');
                    }}>
                    {({ values, setFieldValue, isSubmitting }) => (
                        <Form>
                            <div className="md:grid md:grid-cols-2 md:gap-x-6">
                                <div>
                                    <InputText name="affiliateName" label="Name" />
                                    <InputText name="affiliateEmail" label="Email" />
                                    <InputText name="affiliateEmail2" label="Email (Secondary)" />
                                    <InputSelect name="companyId" label="Company" items={companyList(-1)} optional />
                                    <InputSelect name="sourceId" label="Source" items={sources} textField="sourceName" optional />
                                    <InputText name="verificationCode" label="Verification Code (enter without the spaces)" disabled postIcon="sync-alt" postClick={() => regenCode(setFieldValue)} />
                                    <InputCheckbox name="viewOwnBookings" label="View Own Bookings" className="mt-4" />
                                </div>
                                <div>
                                    <div className="font-extrabold text-white bg-blue-600 mt-4 mb-2 py-2 -mx-4 md:mx-0 px-6">Logo</div>
                                    {data.photoUrl && (
                                        <div>
                                            <AvatarEditor image={`${data.photoUrl}?q=${new Date().getTime()}`} width={200} height={200} border={1} scale={1} rotate={0} />
                                        </div>
                                    )}

                                    <div>
                                        <div className="ml-2 mt-4 mb-2 leading-3 text-sm font-semibold">Change Logo</div>
                                        <div className="flex flex-row gap-x-4 items-center">
                                            <AvatarEditor ref={setRef} image={image} width={200} height={200} border={1} scale={scale} rotate={rotate} />
                                            <div>
                                                <div>Zoom:</div>
                                                <div>
                                                    <input name="scale" type="range" onChange={handleScale} min="0.2" max="3" step="0.01" defaultValue="1" />
                                                </div>
                                                <div>Rotate:</div>
                                                <div className="space-x-2">
                                                    <span className="fad fa-chevron-left fa-2x cursor-pointer" onClick={rotateLeft}></span>
                                                    <span className="fad fa-chevron-right fa-2x cursor-pointer" onClick={rotateRight}></span>
                                                </div>
                                                <div>
                                                    <input name="newImage" type="file" onChange={handleNewImage} />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="font-extrabold text-white bg-blue-600 mt-4 mb-2 py-2 -mx-4 md:mx-0 px-6">Codes</div>
                                    <FieldArray name="codes">
                                        {({ remove, push, replace }) => (
                                            <>
                                                <table className="w-full">
                                                    <thead>
                                                        <tr className="text-left text-sm text-gray-500">
                                                            <th>Code</th>
                                                            <th>Promo Code</th>
                                                            <th>Expiry Date</th>
                                                            <th></th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {(values.codes || []).map((code, index) => (
                                                            <tr key={index}>
                                                                <td className="px-1">
                                                                    <div className="flex flex-row items-center gap-x-2">
                                                                        <InputText name={`codes.${index}.affiliateCode`} disabled={!code.isNew} />
                                                                        {code.isNew && (
                                                                            <div className="text-xs text-gray-500">
                                                                                <FontAwesomeIcon icon="sync-alt" title="Generate" onClick={() => replace(index, { ...code, affiliateCode: generateAlphaNumericCode() })} />
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </td>
                                                                <td className="px-1">
                                                                    <InputText name={`codes.${index}.discountCode`} />
                                                                </td>
                                                                <td className="px-1">
                                                                    <InputDate name={`codes.${index}.expiryDate`} />
                                                                </td>
                                                                <td className="px-1">{code.isNew && <FontAwesomeIcon icon="times" title="Remove" onClick={() => remove(index)} />}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                                <Button className="mt-4" label="Add" onClick={() => push({ affiliateCode: generateAlphaNumericCode(), discountCode: '', expiryDate: '', isNew: true })} />
                                            </>
                                        )}
                                    </FieldArray>
                                </div>
                            </div>
                            <div className="font-extrabold border-b-2 border-blue-600 my-4 -mx-4"></div>
                            <InputCheckbox name="isDisabled" label="Disabled" className="mt-4" />
                            {error && <div className="validation-error ">{error}</div>}
                            <ButtonSubmit isSubmitting={isSubmitting} className="mt-4" />
                        </Form>
                    )}
                </Formik>
            )}
        </div>
    );
};

export default AffiliateEdit;
